import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  VizPortalDQWDataCatalogProviderProps,
  WarningStatusChip,
  WarningStatusProps
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import * as React from 'react';
import { Query } from 'react-apollo';

import {
  FlowLineageUpstreamDataQualityWarningsQuery,
  FlowLineageUpstreamDataQualityWarningsQueryVariables,
} from '../../../gen-src/queries/FlowLineageUpstreamDataQualityWarningsQuery';
import { FlowLineageUpstreamDataQualityWarningsQueryGql } from '../../queries/FlowLineageUpstreamDataQualityWarningsQueryGql';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { renderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';

interface LineageWarningFlowProps {
  readonly asset: AssetMarkedByIndicator;
  readonly flowVizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddableFlowDataQualityWarningProps = VizPortalDQWDataCatalogProviderProps & LineageWarningFlowProps;

export function EmbeddableFlowDataQualityWarning(props: EmbeddableFlowDataQualityWarningProps): JSX.Element {
  const { featureFlags: { publishedConnectionsLineageEnabled }, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<FlowLineageUpstreamDataQualityWarningsQuery, FlowLineageUpstreamDataQualityWarningsQueryVariables>
          query={FlowLineageUpstreamDataQualityWarningsQueryGql}
          variables={{ flowVizportalUrlId: props.flowVizportalUrlId, first: 10, offset: 0, publishedConnectionsLineageEnabled }}
        >
          {result => renderQueryResult(result, {
            renderLoading: () => renderWarnings(urls, { ...props }),
            renderData: data => renderWarnings(urls, { ...props }, data),
          })}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddableFlowDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  data?: FlowLineageUpstreamDataQualityWarningsQuery
): JSX.Element | null {
  const flow = data && data.flows ? data.flows[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive)
  if (!flow && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = flow
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(flow.id, flow.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings

  const props: WarningStatusProps = {
    subjectArea: 'Flow',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
  }

  return <WarningStatusChip {...props} />
}
